/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import {
	CustomBlock,
	MediaPlacement,
	PreviewBlock,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';
import { useDarkModeColors } from '@atlassian/color-picker';
import {
	CommonLayout,
	getSmartCardFallbackUrl,
	useProductDependencies,
	useTrackedSmartCardState,
} from '@atlassian/company-hub-common';
import {
	CustomImageUploadRenderer,
	getImageTypeToShow,
} from '@atlassian/company-hub-common/media-components';

import { type CommonFrameProps, framesDimensions, FrameSizes } from '../../../types';
import { CommonCarouselWrapperStyles, NavigationThumbnailWrapper } from '../styled';

import { messages } from './messages';

type NavigationThumbnailProps = {
	hasRoundedCorners?: boolean;
	isSelected?: boolean;
	maxWidth?: number;
	minWidth?: number;
} & CommonFrameProps;

export const NavigationThumbnail = ({
	cardId,
	link,
	title,
	imageSrc,
	isSelected,
	contentId,
	isEmpty,
	minWidth,
	maxWidth,
	wrapperHeight,
	hasRoundedCorners,
	layout,
	backgroundColor: savedBackgroundColor,
	onFailed,
	onSucceeded,
}: NavigationThumbnailProps) => {
	const { isRestricted, isUnauthorized, trackAndCategorizeError, trackSuccess } =
		useTrackedSmartCardState(cardId, link, onSucceeded, onFailed);
	const intl = useIntl();
	const { isSSRProductInDarkMode } = useProductDependencies();

	const { showUnsplashImage, showExternalSiteImage, showCustomUploadedImage } = getImageTypeToShow({
		imageSrc,
	});
	const { getDarkModeCompatibleBackgroundColor } = useDarkModeColors(isSSRProductInDarkMode);
	const backgroundColor = getDarkModeCompatibleBackgroundColor(savedBackgroundColor ?? '#FFFFFF');
	const substituteBackgroundColorForImage =
		layout === CommonLayout.NO_IMAGE_BACKGROUND && backgroundColor;

	const showCustomTitleUnauthed = isUnauthorized && !!title;
	const showCustomTitleRestricted = isRestricted && !!title;

	const fallbackUrl = getSmartCardFallbackUrl();

	return (
		<CommonCarouselWrapperStyles isSelected={isSelected}>
			<NavigationThumbnailWrapper
				minWidth={minWidth ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].minWidth}
				maxWidth={maxWidth ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].maxWidth}
				height={wrapperHeight ?? framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL].height}
				hasRoundedCorners={hasRoundedCorners}
			>
				<SmartCardSSR
					id={cardId}
					appearance="inline"
					frameStyle="show"
					platform="web"
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: false,
						size: SmartLinkSize.Large,
					}}
					url={link || fallbackUrl}
					onResolve={trackSuccess}
					onError={trackAndCategorizeError}
				>
					{substituteBackgroundColorForImage && (
						<CustomBlock>
							<div
								css={[customBackgroundStyles.backgroundColorOnly]}
								style={{ backgroundColor }}
								data-testid="backgroundColorBlock"
							/>
						</CustomBlock>
					)}
					{!substituteBackgroundColorForImage && (showUnsplashImage || showExternalSiteImage) && (
						<PreviewBlock
							ignoreContainerPadding
							placement={MediaPlacement.Left}
							overrideUrl={imageSrc}
						/>
					)}
					{!substituteBackgroundColorForImage && showCustomUploadedImage ? (
						<CustomBlock css={[customImageUploadStyles, carouselV2CustomImageWidth]}>
							<CustomImageUploadRenderer contentId={contentId} imageSrc={imageSrc} />
						</CustomBlock>
					) : null}
					<TitleBlock
						hideIcon
						text={
							// a link with no title should expect undefined
							isEmpty ? title || intl.formatMessage(messages.emptyStatePlaceholderTitle) : title
						}
						maxLines={2}
						anchorTarget="_self"
						css={[
							titleBlockStylesV2,
							(showCustomTitleUnauthed || showCustomTitleRestricted) &&
								titleBlockHideUnauthorizedStyles,
							isEmpty && !title
								? titleBlockLinkTextSubtlestColorStyles
								: titleBlockLinkTextColorStyles,
						]}
					/>
				</SmartCardSSR>
			</NavigationThumbnailWrapper>
		</CommonCarouselWrapperStyles>
	);
};

const titleBlockLinkTextColorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		color: token('color.text'),
	},
});

const titleBlockLinkTextSubtlestColorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		color: token('color.text.subtlest'),
	},
});

const titleBlockHideUnauthorizedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'div[data-smart-element-group]': {
		display: 'none',
	},
});

const titleBlockStylesV2 = css({
	marginTop: token('space.0'),
	marginLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'[data-smart-element-link]': {
		fontWeight: token('font.weight.regular'),
		flexShrink: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'a, a:visited, a:focus, a:hover, a:active': {
		textDecoration: 'none',
		pointerEvents: 'none',
	},
	width: '100%',
});

const customImageUploadStyles = css({
	position: 'absolute',
	width: '72px',
	top: token('space.0'),
	bottom: token('space.0'),
	left: token('space.0'),
});

const customBackgroundStyles = cssMap({
	backgroundColorOnly: {
		position: 'absolute',
		width: 'var(--preview-block-width)',
		top: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
	},
});

const carouselV2CustomImageWidth = css({
	width: token('space.600'),
});
